import { inject, Injectable } from "@angular/core";
import { Actions, ofType, createEffect } from "@ngrx/effects";
import { of } from "rxjs";
import { catchError, map, switchMap, withLatestFrom, } from "rxjs/operators";
import * as trackingAction from "./tracking.actions";
import { EmptyAction } from "..";
import { SnackBarService } from "../../services/snackbar.service";
import { TrackingService } from "../../services/tracking.service";
import { Store } from "@ngrx/store";
import { FetchBookWithChapterAction, FetchUserBooksAction } from "../user/user.actions";
import { selectBooksPagination } from "../user/user.reducer";


@Injectable()
export class TrackingEffect {
    // Inject Services and Actions
    private actions$ = inject(Actions);
    private trackingService = inject(TrackingService);
    private store = inject(Store);
    private snackBarService = inject(SnackBarService);

    // Read A Book
    readABook$ = createEffect(() =>
        this.actions$.pipe(
            ofType(trackingAction.ReadABookAction),
            switchMap((props) => {
                return this.trackingService.readABook(props.bookId).pipe(
                    map((res) => {
                        return trackingAction.ReadABookSuccessAction();
                    }),
                    catchError(err => {
                        return of(trackingAction.ReadABookFailureAction({
                            error: err.error
                        }))
                    })
                );
            })
        )
    );

    // Read A Book Success
    readABookSuccess$ = createEffect(() =>
        this.actions$.pipe(
            ofType(trackingAction.ReadABookSuccessAction),
            withLatestFrom(this.store.select(selectBooksPagination)),
            switchMap(([_, pagination]) => {
                const pageNumber = pagination?.PageNumber ?? 1;
                const pageSize = pagination?.PageSize ?? 10;
                this.store.dispatch(FetchUserBooksAction({ pageNumber, pageSize }));
                return of(EmptyAction());
            })
        )
    );
    
    // Read A Book Failure
    readABookFailure$ = createEffect(() =>
        this.actions$.pipe(
            ofType(trackingAction.ReadABookFailureAction),
            switchMap((props) => {
                console.error(props);
                return of(EmptyAction());
            })
        )
    );


    // Read A Page
    readAPage$ = createEffect(() =>
        this.actions$.pipe(
            ofType(trackingAction.ReadAPageAction),
            switchMap((props) => {
                return this.trackingService.readAPage(props.pageId, props.timeSpent).pipe(
                    map((res) => {
                        return trackingAction.ReadAPageSuccessAction();
                    }),
                    catchError(err => {
                        return of(trackingAction.ReadAPageFailureAction({
                            error: err.error
                        }))
                    })
                );
            })
        )
    );

    // Read A Page Success
    readAPageSuccess$ = createEffect(() =>
        this.actions$.pipe(
            ofType(trackingAction.ReadAPageSuccessAction),
            switchMap((props) => {
                return of(EmptyAction());
            })
        )
    );

    // Read A Page Failure
    readAPageFailure$ = createEffect(() =>
        this.actions$.pipe(
            ofType(trackingAction.ReadAPageFailureAction),
            switchMap((props) => {
                console.error(props);
                return of(EmptyAction());
            })
        )
    );


    // Read A Chapter
    readAChapter$ = createEffect(() =>
        this.actions$.pipe(
            ofType(trackingAction.ReadAChapterAction),
            switchMap((props) => {
                return this.trackingService.readAChapter(props.chapterId).pipe(
                    map((res) => {
                        return trackingAction.ReadAChapterSuccessAction({ bookId: props.bookId, fromChapterChange: props.fromChapterChange });
                    }),
                    catchError(err => {
                        return of(trackingAction.ReadAChapterFailureAction({
                            error: err.error
                        }))
                    })
                );
            })
        )
    );

    // Read A Chapter Success
    readAChapterSuccess$ = createEffect(() =>
        this.actions$.pipe(
            ofType(trackingAction.ReadAChapterSuccessAction),
            switchMap((props) => {
                this.store.dispatch(FetchBookWithChapterAction({ bookId: props.bookId, fromEffect: props.fromChapterChange }))
                return of(EmptyAction());
            })
        )
    );

    // Read A Chapter Failure
    readAChapterFailure$ = createEffect(() =>
        this.actions$.pipe(
            ofType(trackingAction.ReadAChapterFailureAction),
            switchMap((props) => {
                console.error(props);
                return of(EmptyAction());
            })
        )
    );

    // Complete A Chapter
    completeAChapter$ = createEffect(() =>
        this.actions$.pipe(
            ofType(trackingAction.CompleteAChapterAction),
            switchMap((props) => {
                return this.trackingService.completeAChapter(props.chapterId).pipe(
                    map((res) => {
                        return trackingAction.CompleteAChapterSuccessAction({ bookId: props.bookId, fromChapterChange: props.fromChapterChange });
                    }),
                    catchError(err => {
                        return of(trackingAction.CompleteAChapterFailureAction({
                            error: err.error
                        }))
                    })
                );
            })
        )
    );

    // Complete A Chapter Success
    completeAChapterSuccess$ = createEffect(() =>
        this.actions$.pipe(
            ofType(trackingAction.CompleteAChapterSuccessAction),
            switchMap((props) => {
                this.store.dispatch(FetchBookWithChapterAction({ bookId: props.bookId, fromEffect: props.fromChapterChange }));
                return of(EmptyAction());
            })
        )
    );

    // Complete A Chapter Failure
    completeAChapterFailure$ = createEffect(() =>
        this.actions$.pipe(
            ofType(trackingAction.CompleteAChapterFailureAction),
            switchMap((props) => {
                console.error(props);
                return of(EmptyAction());
            })
        )
    );

    // Complete A Book
    completeABook$ = createEffect(() =>
        this.actions$.pipe(
            ofType(trackingAction.CompleteABookAction),
            switchMap((props) => {
                return this.trackingService.completeABook(props.bookId).pipe(
                    map((res) => {
                        return trackingAction.CompleteABookSuccessAction();
                    }),
                    catchError(err => {
                        return of(trackingAction.CompleteABookFailureAction({
                            error: err.error
                        }))
                    })
                );
            })
        )
    );

    // Complete A Book Success
    completeABookSuccess$ = createEffect(() =>
        this.actions$.pipe(
            ofType(trackingAction.CompleteABookSuccessAction),
            withLatestFrom(this.store.select(selectBooksPagination)),
            switchMap(([_, pagination]) => {
                this.snackBarService.showSnackBar("success", "Well done! You've completed this book.");
                const pageNumber = pagination?.PageNumber ?? 1;
                const pageSize = pagination?.PageSize ?? 10;
                this.store.dispatch(FetchUserBooksAction({ pageNumber, pageSize }));
                return of(EmptyAction());
            })
        )
    );

    // Complete A Book Failure
    completeABookFailure$ = createEffect(() =>
        this.actions$.pipe(
            ofType(trackingAction.CompleteABookFailureAction),
            switchMap((props) => {
                console.error(props);
                return of(EmptyAction());
            })
        )
    );
}
